import React, { useState, useEffect } from 'react';
import { tableStyles } from '../../styles/tableStyles';
import { commonStyles } from '../../styles/commonStyles';
import { API_URL } from '../../utils/constants';

function UserManagement({ token }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        alert('사용자 목록 로드 실패');
      }
    } catch (err) {
      console.error('사용자 로드 실패:', err);
      alert('사용자 목록 로드 중 오류 발생');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleUserRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    if (!window.confirm(`이 사용자를 ${newRole === 'admin' ? '관리자' : '일반 사용자'}로 변경하시겠습니까?`)) return;

    try {
      const response = await fetch(`${API_URL}/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });
      if (response.ok) {
        alert('권한이 변경되었습니다.');
        fetchUsers();
      } else {
        alert('권한 변경 실패');
      }
    } catch (err) {
      console.error('권한 변경 실패:', err);
      alert('권한 변경 중 오류 발생');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('정말로 이 사용자를 삭제하시겠습니까?\n삭제된 사용자는 복구할 수 없습니다.')) return;
    
    try {
      const response = await fetch(`${API_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('사용자가 삭제되었습니다.');
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.error || '사용자 삭제 실패');
      }
    } catch (err) {
      console.error('사용자 삭제 실패:', err);
      alert('사용자 삭제 중 오류 발생');
    }
  };

  const changeUserPassword = async (userId, username) => {
    const newPassword = prompt(`${username}의 새 비밀번호를 입력하세요 (최소 4자):`);
    if (!newPassword) return;

    if (newPassword.length < 4) {
      alert('비밀번호는 최소 4자 이상이어야 합니다.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/users/${userId}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: newPassword })
      });

      if (response.ok) {
        alert('비밀번호가 변경되었습니다.');
      } else {
        const error = await response.json();
        alert(error.error || '비밀번호 변경 실패');
      }
    } catch (err) {
      console.error('비밀번호 변경 실패:', err);
      alert('비밀번호 변경 중 오류 발생');
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    
    console.log('addUser 호출됨:', newUser);
    
    if (!newUser.username || !newUser.password || !newUser.confirmPassword) {
      console.log('필드 체크 실패:', {
        username: newUser.username,
        password: newUser.password,
        confirmPassword: newUser.confirmPassword
      });
      alert('모든 필드를 입력해주세요.');
      return;
    }

    if (newUser.password.length < 4) {
      alert('비밀번호는 최소 4자 이상이어야 합니다.');
      return;
    }

    if (newUser.password !== newUser.confirmPassword) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: newUser.username,
          password: newUser.password,
          role: newUser.role
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert('사용자가 추가되었습니다.');
        setNewUser({ username: '', password: '', confirmPassword: '', role: 'user' });
        setShowAddForm(false);
        fetchUsers();
      } else {
        console.error('서버 응답:', data);
        alert(data.error || '사용자 추가 실패');
      }
    } catch (err) {
      console.error('사용자 추가 실패:', err);
      alert('사용자 추가 중 오류 발생: ' + err.message);
    }
  };

  // 검색 필터링
  const filteredUsers = users.filter(user => {
    const term = searchTerm.toLowerCase();
    return (user.username || '').toLowerCase().includes(term) ||
           (user.role || '').toLowerCase().includes(term);
  });

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '16px',
        color: '#7f8c8d'
      }}>
        사용자 목록을 불러오는 중...
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px'
    }}>
      {/* 헤더 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{
          margin: '0 0 10px 0',
          fontSize: '28px',
          fontWeight: 'bold',
          color: '#2c3e50'
        }}>
          계정 관리
        </h2>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: '#7f8c8d'
        }}>
          시스템 사용자의 권한을 관리하고 계정을 관리할 수 있습니다.
        </p>
      </div>

      {/* 사용자 추가 폼 */}
      {showAddForm && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '30px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '2px solid #3498db'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, fontSize: '20px', color: '#2c3e50' }}>새 사용자 추가</h3>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              style={{
                padding: '6px 12px',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              취소
            </button>
          </div>
          
          <form onSubmit={addUser} style={{ display: 'grid', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
                아이디 *
              </label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="로그인에 사용할 아이디"
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
                required
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
                비밀번호 *
              </label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="최소 4자 이상"
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
                required
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
                비밀번호 확인 *
              </label>
              <input
                type="password"
                value={newUser.confirmPassword}
                onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                placeholder="비밀번호 재입력"
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
                required
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
                권한
              </label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
              >
                <option value="user">일반 사용자</option>
                <option value="admin">관리자</option>
              </select>
            </div>

            <button
              type="submit"
              style={{
                padding: '12px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                marginTop: '10px'
              }}
            >
              사용자 추가
            </button>
          </form>
        </div>
      )}

      {/* 메인 컨텐츠 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        {/* 검색 및 통계 */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <input 
            type="text" 
            placeholder="🔍 아이디, 권한으로 검색..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            style={{
              flex: 1,
              padding: '12px 16px',
              fontSize: '14px',
              border: '2px solid #ecf0f1',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3498db'}
            onBlur={(e) => e.target.style.borderColor = '#ecf0f1'}
          />
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap'
            }}
          >
            ➕ 사용자 추가
          </button>
        </div>

        <div style={{
          padding: '12px 16px',
          backgroundColor: '#ecf0f1',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#2c3e50'
        }}>
          전체 사용자: <strong>{users.length}</strong>명
          <span style={{ marginLeft: '20px' }}>표시: <strong>{filteredUsers.length}</strong>명</span>
          <span style={{ marginLeft: '20px' }}>
            관리자: <strong>{users.filter(u => u.role === 'admin').length}</strong>명
          </span>
        </div>

        {/* 테이블 */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              <tr>
                <th style={{
                  backgroundColor: '#34495e',
                  color: 'white',
                  padding: '16px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderTopLeftRadius: '8px'
                }}>
                  ID
                </th>
                <th style={{
                  backgroundColor: '#34495e',
                  color: 'white',
                  padding: '16px',
                  textAlign: 'left',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  아이디
                </th>
                <th style={{
                  backgroundColor: '#34495e',
                  color: 'white',
                  padding: '16px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  권한
                </th>
                <th style={{
                  backgroundColor: '#34495e',
                  color: 'white',
                  padding: '16px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderTopRightRadius: '8px'
                }}>
                  관리
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#95a5a6',
                    fontSize: '14px'
                  }}>
                    검색 결과가 없습니다.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => (
                  <tr 
                    key={user.id}
                    style={{
                      borderBottom: '1px solid #ecf0f1',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <td style={{ padding: '16px', color: '#7f8c8d' }}>
                      {user.id}
                    </td>
                    <td style={{ padding: '16px', fontWeight: '600', color: '#2c3e50' }}>
                      {user.username}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '6px 16px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: user.role === 'admin' ? '#e8f5e9' : '#e3f2fd',
                        color: user.role === 'admin' ? '#2e7d32' : '#1565c0',
                        border: `1px solid ${user.role === 'admin' ? '#4caf50' : '#2196f3'}`
                      }}>
                        {user.role === 'admin' ? '👑 관리자' : '👤 사용자'}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button 
                          onClick={() => toggleUserRole(user.id, user.role)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
                        >
                          권한 변경
                        </button>
                        <button 
                          onClick={() => changeUserPassword(user.id, user.username)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#f39c12',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#d68910'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#f39c12'}
                        >
                          비밀번호
                        </button>
                        <button 
                          onClick={() => deleteUser(user.id)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#c0392b'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#e74c3c'}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default UserManagement;
